import {
  Accordion,
  AccordionSummary,
  Box,
  Button,
  Grid,
  TableContainer,
  Table,
  TextField,
  AccordionDetails,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  Checkbox,
  Typography,
  ListItemText,
  FormControl,
  FormControlLabel,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import React, { useState, useEffect } from "react";
import { useSnackbar } from "notistack";

import CardInfo from "../../Dashboard/components/Card/CardInfo";
import AuthService from "../../../AuthService";
import Utils from "../../../utils";

export default function FecharConta() {
  const { enqueueSnackbar } = useSnackbar();
  const [numeroIdentificador, setNumeroIdentificador] = useState("");
  const [listaPedidos, setListaPedidos] = useState([]);
  const [listaPedidoPorPessoa, setListaPedidoPorPessoa] = useState([]);
  const [listaPedidosSelecionados, setListaPedidosSelecionados] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [buscando, setBuscando] = useState(false);
  const [erroBusca, setErroBusca] = useState(null);

  useEffect(() => {
    const selecionados = listaPedidoPorPessoa.flatMap((p) =>
      p.listaPedidos.filter((pedido) => pedido.checked)
    );
    setListaPedidosSelecionados(selecionados);
  }, [listaPedidoPorPessoa]);

  const handleChange = (e) => setNumeroIdentificador(e.target.value);

  const handleBuscarPedido = () => {
    setBuscando(true);
    new AuthService()
      .get(
        `/pedido/buscarContasEmAberto?mesa=${numeroIdentificador}&statusIntegracao=PENDENTE&status=PENDENTE&max=200`
      )
      .then((lista) => {
        if (lista.length === 0)
          setErroBusca("Não existe contas em aberta nessa mesa");
        else setErroBusca(null);

        const agrupado = agruparPorPessoa(lista);
        setListaPedidos(lista);
        setListaPedidoPorPessoa(agrupado);
        setListaPedidosSelecionados([]);
      })
      .catch(console.error)
      .finally(() => setBuscando(false));
  };

  const agruparPorPessoa = (pedidos) => {
    const mapa = new Map();

    pedidos.forEach((pedido) => {
      pedido.checked = false;
      const nome = pedido.nomeCliente;
      if (!mapa.has(nome)) {
        mapa.set(nome, {
          nome,
          telefone: pedido.pessoa?.telefone || "",
          checked: false,
          listaPedidos: [],
        });
      }
      mapa.get(nome).listaPedidos.push(pedido);
    });

    return Array.from(mapa.values());
  };

  const handleCalculaTotal = (pedidos) =>
    Utils.formatNumber(
      pedidos.reduce((total, p) => total + Utils.getTotalPedido(p), 0)
    );

  const handleSelecionaPedidoIndividual = (e, pedido) => {
    pedido.checked = e.target.checked;
    setListaPedidoPorPessoa([...listaPedidoPorPessoa]);
  };

  const handleSelecionaTodosOsPedidos = (e, pessoa) => {
    const checked = e.target.checked;
    pessoa.checked = checked;
    pessoa.listaPedidos.forEach((pedido) => (pedido.checked = checked));
    setListaPedidoPorPessoa([...listaPedidoPorPessoa]);
  };

  const handleSelecionaTodasAsPessoas = (e) => {
    const checked = e.target.checked;
    const atualizada = listaPedidoPorPessoa.map((pessoa) => ({
      ...pessoa,
      checked,
      listaPedidos: pessoa.listaPedidos.map((pedido) => ({
        ...pedido,
        checked,
      })),
    }));
    setListaPedidoPorPessoa(atualizada);
  };

  const handleMarcarComoPago = (pedidos) => {
    const pedidosBody = pedidos.map((p) => ({ id: p.id }));
    new AuthService()
      .post(`/pedido/marcarComoPago`, pedidosBody)
      .then(() => {
        enqueueSnackbar("Conta fechada com sucesso", { variant: "success" });
        setListaPedidoPorPessoa([]);
        setListaPedidosSelecionados([]);
        handleBuscarPedido();
      })
      .catch(console.error);
  };

  const keyDown = (e) => {
    if (e === "click" || e.key === "Enter") handleBuscarPedido();
  };

  return (
    <Box p={2}>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid
          size={{
            xs: 12,
            md: 8
          }}>
          <TextField
            value={numeroIdentificador || ""}
            onChange={handleChange}
            onKeyPress={keyDown}
            fullWidth
            autoFocus
            size="small"
            error={!!erroBusca}
            helperText={erroBusca}
            placeholder="Caso queira mais de uma mesa, separe com vírgula. Ex: 60, 80"
            variant="outlined"
            label="Digite o número da mesa"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disabled={buscando || !numeroIdentificador}
            onClick={handleBuscarPedido}
          >
            {buscando ? "Buscando..." : "Buscar"}
          </Button>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 2
          }}>
          <Button
            fullWidth
            size="large"
            variant="contained"
            color="secondary"
            onClick={() => handleMarcarComoPago(listaPedidosSelecionados)}
            disabled={!listaPedidosSelecionados.length}
          >
            Fechar conta
          </Button>
        </Grid>

        {!!listaPedidosSelecionados.length && (
          <Grid
            size={{
              xs: 12,
              md: 2
            }}>
            <CardInfo
              caption="Total selecionado"
              variant="h5"
              value={handleCalculaTotal(listaPedidosSelecionados)}
              valueColor="text.primary"
            />
          </Grid>
        )}

        {!!listaPedidos.length && (
          <Grid
            size={{
              xs: 12,
              md: 2
            }}>
            <CardInfo
              caption="Total"
              variant="h5"
              value={handleCalculaTotal(listaPedidos)}
              valueColor="primary.main"
            />
          </Grid>
        )}
      </Grid>
      {!!listaPedidos.length && (
        <Box pl={4} pt={2}>
          <FormControl>
            <FormControlLabel
              control={<Checkbox onChange={handleSelecionaTodasAsPessoas} />}
              label="Selecionar todas as contas"
            />
          </FormControl>
        </Box>
      )}
      <Box pt={2}>
        {listaPedidoPorPessoa.map((pessoa) => (
          <Accordion
            key={pessoa.nome}
            expanded={expanded === pessoa.nome}
            onChange={(e, isExpanded) =>
              setExpanded(isExpanded ? pessoa.nome : false)
            }
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Grid container alignItems="center">
                <Grid size={1}>
                  <FormControlLabel
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    control={
                      <Checkbox
                        checked={pessoa.checked}
                        onChange={(e) =>
                          handleSelecionaTodosOsPedidos(e, pessoa)
                        }
                      />
                    }
                  />
                </Grid>
                <Grid size={4}>
                  <Typography>{pessoa.nome?.toUpperCase()}</Typography>
                </Grid>
                <Grid size={3}>
                  <Typography>{pessoa.telefone}</Typography>
                </Grid>
                <Grid size={2}>
                  <Typography>
                    {handleCalculaTotal(pessoa.listaPedidos)}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell />
                      <TableCell>Pedido id</TableCell>
                      <TableCell />
                      <TableCell>Data</TableCell>
                      <TableCell>Produto</TableCell>
                      <TableCell align="right">Quantidade</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pessoa.listaPedidos.map((pedido) => (
                      <React.Fragment key={pedido.id}>
                        {pedido.itens.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <Checkbox
                                checked={pedido.checked}
                                onChange={(e) =>
                                  handleSelecionaPedidoIndividual(e, pedido)
                                }
                              />
                            </TableCell>
                            <TableCell>{pedido.id}</TableCell>
                            <TableCell>
                              <ListItemText
                                primary={
                                  <Typography>
                                    {pedido.identificador.numero}
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                  >
                                    {pedido.identificador.tipo}
                                  </Typography>
                                }
                              />
                            </TableCell>
                            <TableCell>
                              {Utils.formatarDataHora(item.createdAt)}
                            </TableCell>
                            <TableCell>
                              {item.produto.nome?.toUpperCase()}
                            </TableCell>
                            <TableCell align="right">
                              {item.quantidade}
                            </TableCell>
                            <TableCell align="right">
                              {Utils.formatNumber(item.valorTotal)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
}
